import Honeybadger from '@honeybadger-io/js';

import { initApi, initCrazyAI } from './mainMachine';
import { askQuery } from '../ai/query';
import { ACTIONS } from './actions';
import {
  extractContent,
  getTagCount,
  removeSVGs,
  restoreSVGs,
  removeComments,
  getInnerHtml,
  changeInnerHtml,
  isStringHTML,
  getElementFromString,
  toDataURL,
} from '../utils/html';
import { formatChecksForAI, getVoice, withRetry } from '../utils/utils';
import { capitalizeFirstLetter } from '../utils/string';
import { LUCKY, AI_IMAGE_MODEL, AI_IMAGE_API, AI_PROMPTS } from '../editor-ui/constants';
import { hasSearchParam, setSearchParam, getSearchParam } from '../utils/location';
import { getSegmentAsText } from '../utils/segments';
import { getUserTimeZone } from '../utils/date';

let channel;

export async function apiInit() {
  const api = initApi();
  const anonymousId = await api.getAnonymousId();
  const userInfo = await api.getUserInfo();
  let editorId = getSearchParam('editorid');

  if (!editorId && userInfo?.guestId) {
    editorId = await api.getLastSession();
    if (editorId) {
      setSearchParam('editorid', editorId);
    }
  }

  // let other tabs know that we have just logged in
  if (hasSearchParam('login') && editorId) {
    channel.postMessage({ msg: 'login', editorId });
  }

  return {
    api,
    anonymousId,
    userId: userInfo?.guestId,
    userEmail: userInfo?.email,
    timeZone: getUserTimeZone(userInfo),
  };
}

export async function sharingSessionInit({ input: { id, pageId, versionId, api } }) {
  await api.loadEditor(id, pageId, versionId);
  const editor = await api.getEditor();
  const pageContext = await api.getAIContext();
  const capabilities = await api.getCapabilities(editor.id);
  const domain = pageId ? editor.pages.find((p) => p.id === pageId).domain : editor.pages[0].domain;
  const brandGuidelines = await api.getBrandGuidelines(domain);

  return { editor, pageId: pageId || editor.pages[0].id, versionId, pageContext, capabilities, brandGuidelines };
}

export async function aiInit() {
  const ai = await initCrazyAI();

  return { ai };
}

export function broadcastChannelListener({ sendBack }) {
  channel = new BroadcastChannel('editor-channel');

  channel.onmessage = (event) => {
    const editorId = getSearchParam('editorid');
    if (event.data.msg === 'login' && event.data.editorId === editorId) {
      sendBack({ type: ACTIONS.MARK_WINDOW_STALE });
    }
  };

  return () => {
    channel.close();
  };
}

export async function apiEventListeners({ sendBack, input }) {
  const { api } = input;
  const elementSelected = (event) => {
    sendBack({ type: ACTIONS.ELEMENT_SELECTED, el: event.detail });
  };
  const elementUnSelected = () => {
    sendBack({ type: ACTIONS.ELEMENT_UNSELECTED });
  };
  const selectedElementUpdated = (event) => {
    sendBack({ type: ACTIONS.SELECTED_ELEMENT_UPDATED, el: event.detail });
  };
  const popupsDetected = () => {
    sendBack({ type: ACTIONS.POPUPS_DETECTED });
  };
  const editorStatusChange = (event) => {
    sendBack({ type: ACTIONS.STATUS_CHANGE, detail: event.detail });
  };
  const elementLocked = () => {
    sendBack({ type: ACTIONS.ELEMENT_LOCKED });
  };
  const errorDetected = (event) => {
    sendBack({ type: ACTIONS.ERROR_DETECTED, error: event.detail });
  };

  api.addEventListener('elementSelected', elementSelected);
  api.addEventListener('elementUnSelected', elementUnSelected);
  api.addEventListener('selectedElementUpdated', selectedElementUpdated);
  api.addEventListener('popupsDetected', popupsDetected);
  api.addEventListener('editorStatus', editorStatusChange);
  api.addEventListener('elementLocked', elementLocked);
  api.addEventListener('error', errorDetected);

  const removeListeners = () => {
    api.removeListener('elementSelected', elementSelected);
    api.removeListener('elementUnSelected', elementUnSelected);
    api.removeListener('selectedElementUpdated', selectedElementUpdated);
    api.removeListener('popupsDetected', popupsDetected);
    api.removeListener('editorStatus', editorStatusChange);
    api.removeListener('elementLocked', elementLocked);
    api.removeListener('error', errorDetected);
  };

  // just in case we missed the event already
  popupsDetected();

  return () => {
    removeListeners();
  };
}

export async function apiAddPage({ input: { url, api } }) {
  const page = await api.createPage(url);
  const editor = await api.getEditor();
  const pageContext = await api.getAIContext();
  const capabilities = await api.getCapabilities(editor.id);

  const brandGuidelines = await api.getBrandGuidelines(page.domain);

  return { editor, pageId: page.id, pageContext, capabilities, brandGuidelines };
}

export async function apiSave({
  input: { api, asNew, versions, usedVoice, doneManualEdits, segmentChanged, activeVersion },
}) {
  let name = 'Edited';
  if (usedVoice && !doneManualEdits) {
    name = `${capitalizeFirstLetter(usedVoice)}`;
  } else if (!usedVoice && !doneManualEdits && segmentChanged && activeVersion?.name) {
    // don't rename if only the segment changed
    name = activeVersion.name;
  }

  // don't rename if it's the same type of name
  if (!asNew && activeVersion && activeVersion.name.includes(name)) {
    name = activeVersion.name;
  } else {
    const regexp = new RegExp(`^${name}\\s?v?(\\d*)$`);
    const versionNumber = versions.reduce((acc, v) => {
      const m = v.name?.match(regexp);
      if (m) {
        if (Number(m[1]) > acc) {
          return m[1];
        } else if (acc === 0) {
          return 1;
        }
      }
      return acc;
    }, 0);
    if (versionNumber > 0) {
      name = `${name} v${+versionNumber + 1}`;
    }
  }

  let versionId;
  if (asNew) {
    versionId = await api.saveAsNew(name);
  } else {
    versionId = await api.save(name);
  }
  const editor = await api.getEditor();

  return { editor, versionId, asNew };
}

export async function apiSelectVersion({ input: { api, pageId, versionId, domain, ownerId, userId } }) {
  await api.loadVersion(pageId, versionId);

  const pageContext = await api.getAIContext();
  let hasPermissions = false;
  if (ownerId && userId) {
    const permissions = await api.listPermissions(domain, ownerId, userId);
    hasPermissions = hasPublishPermissions(permissions);
  }
  const brandGuidelines = await api.getBrandGuidelines(domain);

  return { pageContext, hasPublishPermissions: hasPermissions, brandGuidelines };
}

export function beforeUnloadListener({ receive }) {
  // to trigger a browser-generated confirmation dialog that asks users to confirm if
  // they really want to leave the page when they try to close or reload it, or navigate somewhere else
  const beforeUnloadHandler = (event) => {
    event.preventDefault();
    event.returnValue = true;
  };
  let listening = false;

  const listen = () => {
    if (!window.isElectron) {
      window.addEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.electronAPI?.showExitPrompt?.(true);
    }
    listening = true;
  };
  const removeListener = () => {
    if (!window.isElectron) {
      window.removeEventListener('beforeunload', beforeUnloadHandler);
    } else {
      window.electronAPI?.showExitPrompt?.(false);
    }
    listening = false;
  };

  receive((event) => {
    if (event.type === ACTIONS.DIRTY_CHANGED) {
      if (event.dirty && !listening) {
        listen();
      } else if (!event.dirty && listening) {
        removeListener();
      }
    }
  });

  return () => {
    removeListener();
  };
}

const makeTask = (t) => () => withRetry(t, 3);
const runAllSettled = (x) => Promise.allSettled(x.map((t) => t()));

export async function generateFullPage({ input: { api, ai, voice, pageContext, segment, voices, brandGuidelines } }) {
  const startTime = performance.now();
  const usedVoice = getVoice(voice, voices); // pick randomly if "lucky" is used

  const { name, details } = getSegmentAsText(segment);
  const segmentText = name === 'Everyone' ? null : `${name}\n${details}`;
  let hadLockedElements = false; // needed for internal metrics
  const elements = await api.getAllEditableElements();
  const allSelectors = [];
  const transformedElements = { headline: [], cta: [], longText: [], image: [] };

  // handle identical and 'similar' text elements
  ['headline', 'cta', 'longText'].forEach((type) =>
    elements[type]?.forEach((el) => {
      if (!hadLockedElements) {
        if (el.html.includes('data-locked=""')) {
          hadLockedElements = true;
        }
      }
      // ignore locked element
      if (el.locked !== true) {
        allSelectors.push(el.selector);
        // totally identical
        const index = transformedElements[type].findIndex((e) => e.html === el.html);
        if (index > -1) {
          transformedElements[type][index].selector.push(el.selector);
        } else {
          // text is identical and tag count identical
          const index2 = transformedElements[type].findIndex(
            (e) => e.text === el.text && getTagCount(removeSVGs(e.html)) === getTagCount(removeSVGs(el.html)),
          );
          if (index2 > -1) {
            transformedElements[type][index2].similar.push(el);
          } else {
            transformedElements[type].push({ ...el, selector: [el.selector], similar: [] });
          }
        }
      } else {
        hadLockedElements = true;
      }
    }),
  );

  // We decided to skip generating images during full page generation
  /*
  // deal with images
  ['headline', 'cta', 'longText', 'other'].forEach((type) =>
    elements[type]?.forEach((el) => {
      if (el.imgProps?.url) {
        if (!hadLockedElements) {
          if (el.html.includes('data-locked=""')) {
            hadLockedElements = true;
          }
        }
        // ignore locked element
        if (el.locked !== true) {
          allSelectors.push(el.selector);
          // find elements with same image
          const index = transformedElements.image.findIndex((e) => e.imgProps?.url === el.imgProps?.url);
          if (index > -1) {
            transformedElements.image[index].selector.push(el.selector);
          } else {
            transformedElements.image.push({ ...el, selector: [el.selector], similar: [] });
          }
        } else {
          hadLockedElements = true;
        }
      }
    }),
  );
  */

  const buildElementTask = ({ type, item, index, groupId }) =>
    makeTask(() => {
      if (type === 'image') {
        return generateImage({
          item,
          index,
          type,
          api,
          ai,
          pageContext,
          voice: usedVoice,
          transformedElements,
          segmentText,
          brandGuidelines,
          groupId,
        });
      }
      return generateElement({
        item,
        index,
        type,
        api,
        ai,
        pageContext,
        voice: usedVoice,
        transformedElements,
        segmentText,
        brandGuidelines,
        groupId,
      });
    });

  function mapDependentToHeadline(item) {
    return transformedElements.headline.findIndex((headline) => headline.text === item.closestHeadline);
  }

  // Gather dependents for a given headline index (or the ones that don't belong to any (-1 index))
  function dependentsForHeadline(headlineIndex) {
    const out = [];
    for (const type of ['cta', 'longText', 'image']) {
      transformedElements[type].forEach((item, i) => {
        if (mapDependentToHeadline(item) === headlineIndex) {
          out.push([type, item, i]);
        }
      });
    }
    return out;
  }

  await api.setLoadingStatus(allSelectors);
  const groupId = await api.openGroup({ voice: usedVoice });

  const headlinePipelines = transformedElements.headline.map((headlineItem, index) =>
    (async () => {
      // 1) Generate the headline (with retry)
      await buildElementTask({ type: 'headline', item: headlineItem, index, groupId })();

      // 2) As soon as it's done, kick off the dependents for THIS headline
      const depTasks = dependentsForHeadline(index).map(([type, item, index]) =>
        buildElementTask({ type, item, index, groupId }),
      );
      // Run this headline's dependents concurrently; don't block other headlines
      await runAllSettled(depTasks);
    })(),
  );

  // Unassigned pipeline: items that don’t belong to any headline
  const unassignedPipeline = runAllSettled(
    dependentsForHeadline(-1).map(([type, item, index]) => buildElementTask({ type, item, index, groupId })),
  );

  await Promise.allSettled([...headlinePipelines, unassignedPipeline]);

  await api.closeGroup(groupId);

  let generatorVoiceUsed = {
    voice: usedVoice,
    user: voices.find((v) => v.name === usedVoice)?.id == null ?? false,
    lucky: voice === LUCKY,
  };

  const elapsedTime = Math.round(performance.now() - startTime);
  console.log('Duration for generation: ', elapsedTime); // eslint-disable-line
  return { generatorVoiceUsed, hadLockedElements, elapsedTime };
}

async function generateElement({
  item,
  index,
  type,
  api,
  ai,
  pageContext,
  voice,
  transformedElements,
  segmentText,
  brandGuidelines,
  groupId,
}) {
  return new Promise((resolve, reject) => {
    const isHTML = isStringHTML(item.html);
    const content = isHTML ? removeComments(item.html) : item.text;

    // const origTagCount = getTagCount(removeSVGs(content));
    const voiceObject = brandGuidelines.voices.find((v) => v.name === voice);

    if (!voiceObject) {
      throw new Error(`Voice "${voice}" not found in brand guidelines`);
    }

    const params = {
      tagName: item.tagName,
      url: pageContext.url,
      content: removeSVGs(content), // AI is very slow to "copy" SVG content, so we remove it before sending it
      title: pageContext.title || '',
      meta_description: pageContext.meta_description || '',
      voice: voiceObject.definition,
      voiceSample: voiceObject.sample,
      suggestion_count: 1,
      element_type: type,
      segment: segmentText,
      siteContext: brandGuidelines.profile.context,
      audience: brandGuidelines.profile.audience,
      checks: JSON.stringify(
        (brandGuidelines.checks?.filter((check) => check.enabled === true) ?? []).map(formatChecksForAI),
      ),
    };

    if (type !== 'headline') {
      params.nearest_headline =
        transformedElements.headline.find((headline) => headline.text === item.closestHeadline)?.newText ?? '';
    }

    askQuery(ai, AI_PROMPTS.TEXT_GENERATOR, params, {
      url: `${location.pathname}${location.search}`,
    })
      .then((text) => {
        let response;
        try {
          response = JSON.parse(text).variations[0]; /*.filter((x) => getTagCount(x) === origTagCount)[0];*/
        } catch {
          // noop
        }

        if (!response) {
          Honeybadger.notify('Unexpected response format from AI.', {
            context: { inputContent: content, response: text },
          });
          api.clearLoadingStatus(item.selector);
          reject('AI response format error.');
          return;
        }
        // restore removed SVGs, and remove data-locked attributes, that the API added
        const newHTML = restoreSVGs(content, response).replaceAll(/data-locked=""/g, '');

        item.selector.forEach((s) => {
          if (isHTML) {
            api.setHTML(newHTML, s, groupId);
          } else {
            api.setText(response, s, null, groupId);
          }
        });
        item.similar.forEach((h) => {
          if (isHTML) {
            const content2 = removeComments(h.html);
            const changed = changeInnerHtml(content2, getInnerHtml(response));

            const result = restoreSVGs(content2, changed);
            api.setHTML(result, h.selector, groupId);
          } else {
            api.setText(response, h.selector, null, groupId);
          }
        });
        if (type === 'headline') {
          if (isHTML) {
            transformedElements.headline[index].newText = extractContent(newHTML);
          } else {
            transformedElements.headline[index].newText = extractContent(response);
          }
        }
        resolve();
      })
      .catch((e) => {
        item.selector.forEach((s) => {
          api.clearLoadingStatus(s);
        });
        Honeybadger.notify(e, 'AI error');
        reject(e);
      });
  });
}

async function generateImage({
  item,
  api,
  ai,
  pageContext,
  voice,
  transformedElements,
  segmentText,
  brandGuidelines,
  groupId,
}) {
  return new Promise((resolve, reject) => {
    const voiceObject = brandGuidelines.voices.find((v) => v.name === voice);

    if (!voiceObject) {
      throw new Error(`Voice "${voice}" not found in brand guidelines`);
    }

    const params = {
      voice: voiceObject.definition,
      title: pageContext.title || '',
      segment: segmentText,
      siteContext: brandGuidelines.profile.context,
      audience: brandGuidelines.profile.audience,
      meta_description: pageContext.meta_description || '',
      nearest_headline:
        transformedElements.headline.find((headline) => headline.text === item.closestHeadline)?.newText ?? '',
      alt_text: getElementFromString(item.html).alt || '',
    };

    askQuery(ai, AI_PROMPTS.IMAGE_GENERATOR, params, {
      url: `${location.pathname}${location.search}`,
    })
      .then(async (text) => {
        const headers = new Headers();
        headers.append('Content-Type', 'application/json');
        const image = await toDataURL(item.imgProps.blob);
        const body = {
          model: AI_IMAGE_MODEL,
          text,
          negativeText: '',
          image,
          numberOfImages: 1,
        };

        const response = await fetch(AI_IMAGE_API, {
          method: 'POST',
          headers,
          body: JSON.stringify(body),
        });
        if (!response.ok) {
          Honeybadger.notify('AI image generation failed', {
            context: { response },
          });
          throw new Error('AI image generation failed.');
        }
        const json = await response.json();
        const newImage = json.images[0];

        item.selector.forEach((s) => {
          api.setImage(newImage, s, null, groupId);
        });

        resolve();
      })
      .catch((e) => {
        item.selector.forEach((s) => {
          api.clearLoadingStatus(s);
        });
        Honeybadger.notify(e, 'AI error');
        reject(e);
      });
  });
}

export async function apiPublish({ input: { api, pageId, versionId } }) {
  await api.publish(pageId, versionId);
}

export async function apiCreateOrValidateUser({ input: { api, userEmail, editorId, redirect } }) {
  const result = await api.createOrValidateUser(userEmail, redirect);

  if (result.sentOwnerEmail || result.sentUserValidationEmail) {
    return result;
  }

  const capabilities = await api.getCapabilities(editorId);
  const userInfo = await api.getUserInfo();

  return {
    ...result,
    capabilities,
    userId: userInfo?.guestId,
    userEmail: userInfo?.email,
    timeZone: getUserTimeZone(userInfo),
  };
}

export async function apiCheckPermissions({ input: { api, redirect } }) {
  const result = await api.checkPermissions(redirect);

  return result;
}

export async function apiCheckInstallation({ input: { api } }) {
  const result = await api.checkScript();
  return result.installed;
}

export async function apiGivePublishAccess({
  input: { api, userId, versionId, pageId, userEmail, redirect, isElectron },
}) {
  const result = await api.givePublishAccess(versionId, pageId, userId, redirect, isElectron);

  const hasPermissions = hasPublishPermissions(result);
  if (!hasPermissions) throw new Error('Incorrect permission values returned.');

  return { userEmail };
}

function hasPublishPermissions(permissions) {
  return (
    permissions.accountExists &&
    ((permissions.siteExists && permissions.canAccessSite) || permissions.canCreateSite) &&
    ['owner', 'manager'].includes(permissions.role)
  );
}

export async function runBrandChecks({ input: { api, ai, checks, el, parentRef }, self }) {
  try {
    const startTime = performance.now();
    const enabledChecks = checks?.filter((check) => check.enabled === true) ?? [];
    const formattedChecks = enabledChecks.map(formatChecksForAI);

    // Don't invoke AI if we have no checks
    if (enabledChecks.length === 0) {
      parentRef.send({ type: ACTIONS.SAVE_CHECKS_RESULTS, results: [], selector: el?.selector, actorId: self.id });
      return;
    }

    const elements = await api.getAllElementsForChecks();

    // Convert elements to the format needed for the AI
    const formattedElements = Object.entries(elements)
      .flatMap(([key, values]) => (values ?? []).map((v) => ({ ...v, type: key })))
      .filter(({ selector }) => !el || selector === el.selector) // if "el" is provided, we only run checks on that
      .map(({ selector, text, type }, index) => {
        return { selector, text, id: String(index), type };
      });

    // Split elements into batches
    const BATCH_SIZE = 30;
    const batches = [];
    for (let i = 0; i < formattedElements.length; i += BATCH_SIZE) {
      batches.push(formattedElements.slice(i, i + BATCH_SIZE));
    }

    // run all batches in parallel
    const results = await Promise.all(
      batches.map(
        (batch) =>
          new Promise((resolve, reject) => {
            askQuery(
              ai,
              AI_PROMPTS.CHECKS,
              {
                checks: JSON.stringify(formattedChecks),
                elements: JSON.stringify(batch.map(({ text, id }) => ({ text, id }))),
              },
              { url: `${location.pathname}${location.search}` },
            )
              .then((text) => {
                try {
                  const parsedResult = JSON.parse(text).failures;
                  resolve(parsedResult);
                } catch (error) {
                  Honeybadger.notify('Unexpected checks response format from AI.', {
                    context: { aiResponse: text },
                  });
                  reject(error);
                }
              })
              .catch((e) => {
                Honeybadger.notify(e, 'AI error');
                reject(e);
              });
          }),
      ),
    );
    let allResults = results.flat();

    // Verify results and add back 'selector' and 'text' as that's used for showing results
    const elementById = new Map(formattedElements.map((e) => [e.id, e]));
    const checkById = new Map(enabledChecks.map((c) => [c.id, c]));

    const { good, bad } = allResults.reduce(
      (acc, item) => {
        const orig = elementById.get(item.id);
        const check = checkById.get(item.check_id);
        if (orig && check) {
          acc.good.push({ ...item, id: null, text: orig.text, selector: orig.selector, checkName: check.name });
        } else {
          acc.bad.push(item);
        }
        return acc;
      },
      { good: [], bad: [] },
    );

    if (bad.length > 0) {
      Honeybadger.notify('Brand check result contains non-existent element or check id', {
        context: { badSelectorResults: bad },
      });
    }

    parentRef.send({ type: ACTIONS.SAVE_CHECKS_RESULTS, results: good, selector: el?.selector, actorId: self.id });
    const elapsedTime = Math.round(performance.now() - startTime);
    console.log('Duration for running checks: ', elapsedTime); // eslint-disable-line
  } catch (error) {
    parentRef.send({ type: ACTIONS.BRAND_CHECKS_ERROR, error, selector: el?.selector, actorId: self.id });
  }
}
